import React from 'react';
import cn from 'classnames';
import styles from './CommandBar.module.scss';
import Contact from '@repo/utils/models/Contact';
import SearchResult from '@app/Views/Search/SearchResult';
import { UserAction } from '@repo/utils/types';
import { GroupIdentifierIcon } from '@repo/icons/GroupIdentifierIcon';
import Group from '@repo/utils/models/Group';
import { Key, KeyWithTrigger } from '@repo/components/tooltips';
import { NavigationItemDetails } from '@layouts/PrimaryLayout/Navigation/NavigationItem';

export type GroupOptionItem = {
  type: 'group';
  group: Group;
  keyboard?: string;
  className?: string;
};
export type ContactOptionItem = { type: 'contact'; contact: Contact; className?: string };
export type NavOptionItem = NavigationItemDetails & { type: 'nav'; className?: string };
type SectionCTAItem = Pick<UserAction, 'title' | 'action' | 'inlineIcon' | 'className'> & {
  type: 'cta';
};
export type MenuOption =
  | UserAction
  | ContactOptionItem
  | GroupOptionItem
  | NavOptionItem
  | SectionCTAItem;

const isEqual = (a: string, b: string): boolean => a && b && a.toLowerCase() == b.toLowerCase();

const ActionOption = ({
  option: { icon: Icon, keyboard, title, subtitle },
  query,
}: {
  option: Partial<UserAction>;
  query?: string;
}) => {
  return (
    <>
      {Icon && (
        <Icon className={styles.itemIcon} hover={false} hoverOpacity={false} color="textPrimary" />
      )}
      <span className={cn(styles.optionLabel, 'option-label', subtitle && styles.withSubtitle)}>
        {title}
        {subtitle && <span className={cn(styles.optionSubtitle, 'information')}>{subtitle}</span>}
      </span>
      {keyboard && (
        <Key className={cn(styles.shortcut, isEqual(query, keyboard) && styles.matched)}>
          {keyboard}
        </Key>
      )}
    </>
  );
};

const SectionCTA = ({ option: { inlineIcon: Icon, title } }: { option: SectionCTAItem }) => {
  return (
    <span className={styles.cta}>
      <Icon className={styles.ctaIcon} />
      <span className={cn(styles.optionLabel, 'text-small')}>{title}</span>
    </span>
  );
};

const GroupOption = ({
  option: { group, keyboard },
  query,
}: {
  option: GroupOptionItem;
  query?: string;
}) => {
  return (
    <>
      <GroupIdentifierIcon className={cn(styles.itemIcon, styles.groupIcon)} group={group} />
      <span className={cn(styles.optionLabel)}>{group.title}</span>
      {keyboard && (
        <KeyWithTrigger
          className={cn(styles.shortcutWithTrigger, isEqual(query, keyboard) && styles.matched)}
          keyChar={keyboard}
        />
      )}
    </>
  );
};

const NavOption = ({
  option: { IconComponent, text, keyChar },
  query,
}: {
  option: NavOptionItem;
  query?: string;
}) => {
  return (
    <>
      <IconComponent
        className={styles.itemIcon}
        hover={false}
        hoverOpacity={false}
        color="textPrimary"
      />
      <span className={styles.optionLabel}>{text}</span>
      {keyChar && (
        <KeyWithTrigger
          className={cn(styles.shortcutWithTrigger, isEqual(query, keyChar) && styles.matched)}
          keyChar={keyChar}
        />
      )}
    </>
  );
};

const ContactOption = ({ option: { contact } }: { option: ContactOptionItem }) => (
  <SearchResult result={contact} />
);

const contentForOption = (option: MenuOption, query: string | null) => {
  switch (option.type) {
    case 'action':
      return <ActionOption option={option} query={query} />;

    case 'group':
      return <GroupOption option={option as GroupOptionItem} query={query} />;

    case 'contact':
      return <ContactOption option={option as ContactOptionItem} />;

    case 'nav':
      return <NavOption option={option as NavOptionItem} query={query} />;

    case 'cta':
      return <SectionCTA option={option as SectionCTAItem} />;

    default:
      return null;
  }
};

function Option({ option, query }: { option: MenuOption; query?: string }) {
  return <>{contentForOption(option, query)}</>;
}

export default function renderOption(menuOption: MenuOption, searchTerm?: string) {
  return <Option option={menuOption} query={searchTerm} />;
}
